import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// ANT DESIGN
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Card,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";

// PROJECT IMPORTS
import styles from "../../../../../assets/styles/styles";
import CustomButton from "../../../../../components/custom/CustomButton";
import { accountStatus } from "../../../../../components/facilities/AccountFacility";
import MainLayout from "../../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../../components/layouts/enhancement/TopContent";
import cnt from "../../../../../services/definedConstant";
import { BOM } from "../../../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../wallet.config";
import { showCheckerApprovalModal } from "../../../../../components/CheckerModal";

const TransferSubToSub = (props) => {
  const { Text, Title } = Typography;
  const { TextArea } = Input;
  const { userData, isSubWallet = false } = props;
  const { session } = userData;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const subWalletList = state?.subWalletList
    ?.filter((item) => item.id !== state.id) // exclude current sub wallet
    .map((item) => ({
      label: `${item.nubanNumber} (${item.name})`,
      value: item.id,
    }));

  function onCancel() {
    navigate(isSubWallet ? "/detail-sub-wallet" : "/detail-wallet-account", {
      state: state,
    });
  }

  const onSubmit = (formValues) => {
    const url = `${apiConfig.WalletAccount}/accounttransfers`;

    formValues.transferDate = BOM.DateTime(
      formValues.transferDate,
      "only-date",
    );
    formValues.fromAccountId = state.id;

    const body = {
      ...formValues,
      dateFormat: "dd MMMM yyyy",
      locale: "en",
      method: "POST",
      session,
    };

    setIsLoading(true);
    BOM.FetchReqAction(
      body,
      url,
      (err, res) => {
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
          setIsLoading(false);
        } else {
          BOM.AlertMsg(cnt.SUCCESS, "Transfer was successful");
          navigate("/detail-sub-wallet", {
            state: { ...state, statusValue: accountStatus.active },
          });
          //STOP LOAD
          setIsLoading(false);
        }
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: (res) => {
          navigate("/detail-sub-wallet", { state: { ...state } });
          setIsLoading(false);
        },
      },
    );
  };

  return (
    <MainLayout pageItems={staticConfig.transferToSubWalletBreadcrumbItems}>
      <TopContent
        pageTitle={labelConfig.subWalletAccount}
        createPageTitle={labelConfig.transferToSubWallet}
      />
      <Spin spinning={isLoading} size="large">
        <Card
          headStyle={{ borderBottomWidth: 0 }}
          style={{
            boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
            borderRadius: 20,
            marginTop: 20,
          }}
        >
          <Form form={form} layout="vertical" onFinish={onSubmit}>
            <Row style={{ justifyContent: "spacebetween", marginTop: "15px" }}>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  name="toAccountId"
                  label={
                    <Text style={{ fontSize: "16px" }}>
                      {labelConfig.toSubWalletAccount}
                    </Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.toSubWalletAccount),
                    },
                  ]}
                >
                  <Select
                    placeholder={BOM.handlePlaceholderMsg(
                      labelConfig.toSubWalletAccount,
                      labelConfig.pleaseEnter,
                    )}
                    style={{ width: "100%", height: "44px" }}
                    options={subWalletList}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  name="transferAmount"
                  label={
                    <Text style={{ fontSize: "16px" }}>
                      {labelConfig.amount}
                    </Text>
                  }
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.amount),
                    },
                  ]}
                >
                  <InputNumber
                    placeholder={BOM.handlePlaceholderMsg(labelConfig.amount)}
                    style={{ width: "100%", height: "44px" }}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) =>
                      value ? value.replace(/(,*)/g, "") : ""
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.transferDate}
                  name={"transferDate"}
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.transferDate),
                    },
                  ]}
                >
                  <DatePicker
                    suffixIcon={<CalendarIcon color="#667085" width={20} />}
                    style={{ width: "100%", height: "44px" }}
                    disabledDate={BOM.DisabledAfterCurrentDate}
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ padding: "0px 8px" }}>
                <Form.Item
                  label={labelConfig.transferDescription}
                  name={"transferDescription"}
                  rules={[
                    {
                      required: true,
                      message: BOM.CheckReqFld(labelConfig.transferDescription),
                    },
                  ]}
                >
                  <TextArea
                    placeholder={BOM.handlePlaceholderMsg(
                      labelConfig.transferDescription,
                    )}
                    autoSize={{
                      minRows: 2,
                      maxRows: 6,
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Divider />
              <CustomButton
                content="Submit"
                style={{
                  width: "10%",
                  marginLeft: "auto",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                  backgroundColor: "#BA24D5",
                  color: "white",
                  border: "1px solid #BA24D5",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
                htmlType="submit"
                backgroundColor="#BA24D5"
                colorText="white"
                hoverBackground="#AD4EA7"
              />

              <CustomButton
                content="Cancel"
                style={{
                  width: "10%",
                  marginLeft: "1%",
                  boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                  backgroundColor: "#BA24D5",
                  color: "white",
                  border: "1px solid #BA24D5",
                  fontSize: "16px",
                  fontWeight: "600",
                }}
                backgroundColor="#fff"
                colorText="black"
                hoverBackground="#F5F5F5"
                onClick={onCancel}
              />
            </Row>
          </Form>
        </Card>
      </Spin>
    </MainLayout>
  );
};

export default TransferSubToSub;
